import { useRef } from 'react';
import {
    exportLayerOriginInfo,
    getBehavior,
    getTrackFunc,
    updateLayerResize,
    useFramework,
    useIdentity,
} from '@/framework';
import type { ITouchKitEvent } from '@/utils/event-kit';
import { translatePointAngle } from '@/utils/math-kit';
import { asyncTraverseTree } from '@/utils/tree-kit';
import {
    BaseLayerViewType,
    DirectionType,
    REFERENCE_LINE_EVENT_NAMES,
    ResizeOperateType,
    RESIZING_ABSORB_DISTANCE,
    SMART_GUIDE_EVENT_NAMES,
    type ILayerProps,
} from '@/version-types';
import { useResizingProxy } from './use-resize-proxy';

export const useResizeOperate = () => {
    const framework = useFramework();
    const identity = useIdentity();
    const oldInfo = useRef<ILayerProps>();
    const originLayerInfo = useRef<ReturnType<typeof exportLayerOriginInfo>>();

    const { proxyResizing, proxyBeforeResizing, proxyAfterResizing } = useResizingProxy();
    return {
        beforeResize(layerId: string, operateType: ResizeOperateType) {
            const layer = framework.getLayer(layerId, true);
            oldInfo.current = layer;
            originLayerInfo.current = exportLayerOriginInfo(identity, layerId);
            framework.events.emitBeforeBaseOperate(layer, BaseLayerViewType.RESIZE);
            if (RESIZING_ABSORB_DISTANCE) {
                proxyBeforeResizing(operateType);
            }
        },

        /**
         * 缩放
         *
         * @param id - 图层 ID
         * @param rotate - 旋转角度
         * @param operateType - 操作类型
         * @param e - 触摸事件对象
         */
        resize(id: string, rotate: number, operateType: ResizeOperateType, e: ITouchKitEvent) {
            if (!oldInfo.current || !originLayerInfo.current) return;
            if (!framework.getLayer(id)) return;

            framework.operate.setIsBaseOperate(true);
            const scale = framework.stage.getScale();

            const isChangeHeight = operateType === ResizeOperateType.TC || operateType === ResizeOperateType.BC;

            // 获取操作方向
            const direction =
                operateType === ResizeOperateType.LC || operateType === ResizeOperateType.TC
                    ? DirectionType.OPPOSITE
                    : DirectionType.POSITIVE;

            let reverse = operateType === ResizeOperateType.LC;
            if (isChangeHeight) {
                reverse = operateType === ResizeOperateType.TC;
            }

            const startPoint = translatePointAngle(e.startPoint, -rotate);
            const currentPoint = translatePointAngle(e.currentPoint, -rotate);

            let offsetY = currentPoint.y - startPoint.y;
            let offsetX = currentPoint.x - startPoint.x;

            // 吸附
            if (RESIZING_ABSORB_DISTANCE) {
                const proxyResult = proxyResizing({
                    displacementX: !isChangeHeight ? Math.floor(offsetX / scale) : 0,
                    displacementY: isChangeHeight ? Math.floor(offsetY / scale) : 0,
                    oldLayer: oldInfo.current,
                    operateType,
                    direction,
                    e,
                });

                offsetX = proxyResult.x * scale;
                offsetY = proxyResult.y * scale;
            }

            let offset = 0;
            if (isChangeHeight) {
                offset = Math.floor(offsetY / scale) * direction;
            } else {
                offset = Math.floor(offsetX / scale) * direction;
            }
            const { childOriginInfoMap, originInfo } = originLayerInfo.current;
            const { width, height, isMin } = updateLayerResize(
                identity,
                id,
                offset,
                direction,
                reverse,
                isChangeHeight,
                originInfo,
                childOriginInfoMap,
                { isStart: e.isStart, isEnd: e.isEnd },
            );
            framework.operate.cursorTip.set({
                type: 'resize',
                x: e.x,
                y: e.y,
                width,
                height,
                isMin,
            });

            if (RESIZING_ABSORB_DISTANCE) {
                proxyAfterResizing(e);
            }

            if (e.isEnd) {
                framework.operate.cursorTip.clear();
                const oldLayer = oldInfo.current;
                if (oldLayer) {
                    let hasEffect = false;
                    asyncTraverseTree(oldLayer, async (layer) => {
                        const result = await getBehavior(identity, layer)?.onBaseResizeEnd?.(layer.id);
                        if (result && !hasEffect) {
                            hasEffect = true;
                        }
                    }).then(() => {
                        if (hasEffect) {
                            const layer = framework.getLayer(id, true);
                            if (layer) {
                                framework.stage.addOrUpdate(layer);
                            }
                        }
                        framework.history.write(oldLayer, getTrackFunc(identity, oldLayer, 'resize'));
                    });
                }
                framework.operate.setIsBaseOperate(false);
                framework.stage.addOrUpdate(framework.getLayer(id, true));
                // 清除智能辅助线
                framework.events.emit(SMART_GUIDE_EVENT_NAMES.SET_BLOCKS, []);
                framework.events.emit(SMART_GUIDE_EVENT_NAMES.SET_LINES, []);
                framework.events.emit(REFERENCE_LINE_EVENT_NAMES.HIGHLIGHT, []);
                framework.operate.setActiveLayer(id);
            }
        },
    };
};
